import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { BrandSiteEngineIndexing } from "@/lib/brand-site-indexing";
import { testBrandSiteSearchEngineCredential, updateBrandSiteSearchEngineCredential } from "@/lib/brand-site-indexing";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string | number;
  brandId: string | number;
  siteId: number;
  engine: BrandSiteEngineIndexing;
};

const GUIDE: Record<string, { title: string; steps: string[] }> = {
  baidu: {
    title: "百度收录（普通收录）",
    steps: [
      "登录站长平台，先添加并验证您的域名。",
      "找到“普通收录/提交URL”等入口，把您要提交的官网地址提交。",
      "复制页面里需要的站点地址与密钥，填到这里保存。",
      "保存后点“测一下”，确认现在能连通对方服务。",
    ],
  },
  so360: {
    title: "360 搜索收录（普通收录）",
    steps: [
      "登录站长平台，先添加并验证您的域名。",
      "找到提交入口，把您要收录的官网地址提交。",
      "复制页面里需要的站点地址与密钥，填到这里保存。",
      "保存后点“测一下”，确认现在能连通对方服务。",
    ],
  },
};

export function SearchEngineCredentialSheet({
  open,
  onOpenChange,
  accountId,
  brandId,
  siteId,
  engine,
}: Props) {
  const guide = useMemo(() => GUIDE[engine.id] ?? { title: "搜索引擎收录", steps: [] }, [engine.id]);

  const [siteHost, setSiteHost] = useState("");
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSiteHost(engine.site_host ?? "");
    // 密钥不回传给页面：已保存时只做占位，不自动填充。
    setSecret("");
  }, [open, engine.site_host]);

  async function onSave() {
    if (saving) return;
    setError(null);
    const trimmedHost = siteHost.trim();
    if (!trimmedHost) {
      setError("请填写站点地址");
      return;
    }

    if (!engine.credential_configured || secret.trim()) {
      if (!secret.trim()) {
        setError("请填写密钥");
        return;
      }
    }

    setSaving(true);
    try {
      await updateBrandSiteSearchEngineCredential({
        accountId,
        brandId,
        siteId,
        engineId: engine.id,
        siteHost: trimmedHost,
        secret: secret.trim(),
      });
      toast.success("已保存密钥与站点地址");
      onOpenChange(false);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "保存失败";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  async function onTest() {
    if (testing) return;
    setError(null);
    setTesting(true);
    try {
      const result = await testBrandSiteSearchEngineCredential({
        accountId,
        brandId,
        siteId,
        engineId: engine.id,
      });
      if (result.ok) toast.success("连通成功");
      else toast.error(result.message || "连通失败");
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "测一下失败";
      setError(message);
      toast.error(message);
    } finally {
      setTesting(false);
    }
  }

  const secretPlaceholder = engine.credential_configured ? "已保存" : "请输入密钥";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{guide.title}</DialogTitle>
          <DialogDescription>把对方平台给的“站点地址”和“密钥”填在这里，我们会替你提交收录。</DialogDescription>
        </DialogHeader>

        <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
          {guide.steps.map((s, idx) => (
            <li key={idx}>{s}</li>
          ))}
        </ol>

        <div className="mt-5 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="brand-site-indexing-site-host">站点地址</Label>
            <Input
              id="brand-site-indexing-site-host"
              value={siteHost}
              onChange={(e) => setSiteHost(e.target.value)}
              disabled={saving || testing}
              placeholder="例如：www.example.com"
              autoComplete="off"
              className="font-mono text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="brand-site-indexing-secret">密钥</Label>
            <Input
              id="brand-site-indexing-secret"
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              disabled={saving || testing}
              placeholder={secretPlaceholder}
              autoComplete="off"
              className="font-mono text-sm"
            />
          </div>
        </div>

        {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}

        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving || testing}>
            取消
          </Button>
          <Button type="button" variant="secondary" onClick={() => void onTest()} disabled={saving || testing}>
            {testing ? "测一下中…" : "测一下"}
          </Button>
          <Button type="button" onClick={() => void onSave()} disabled={saving || testing}>
            {saving ? "保存中…" : "保存"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

